import React, { useState, useEffect } from 'react';
import {
  useGetAgentsQuery,
  useDeleteAgentMutation,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useGetAgentQuery
} from '../redux/services/agentsApi';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { TRI_STATE, TRI_STATE_LABELS, DATE_COMPARISON_LABELS } from '../contants';

// Local storage key for filters
const STORAGE_KEY = 'agentFilters';

// Run Type constants
const RunTypeEnum = {
  CHAT: "C",
  WORKFLOWS: "W"
};

const RUN_TYPES = {
  [RunTypeEnum.CHAT]: 'Chat',
  [RunTypeEnum.WORKFLOWS]: 'Workflows'
};

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      page: 1,
      limit: 25,
    };
  } catch (error) {
    return {
      page: 1,
      limit: 25,
    };
  }
};

const AGENT_TYPES = {
  1: 'Agent for short summary',
  2: 'Agent for short context',
  3: 'Agent for sales',
  4: 'Agent for response validation',
  5: 'Agent for sales resend',
  6: 'Agent for notifications',
  7: 'Agent for clients',
  8: 'Agent for moderators',
  9: 'Agent for routing'
};

const initialAgentForm = {
  url: '',
  name: '',
  token: '',
  enabled: true,
  agent_type: 1,
  run_type: RunTypeEnum.CHAT,
  priority: 0,
  last_marafon: TRI_STATE.NA,
  cur_date: 0,
  plus_days: '',
  cur_date_to: 0,
  plus_days_to: '',
  web1: TRI_STATE.NA,
  web2: TRI_STATE.NA,
  web_faq: TRI_STATE.NA,
  cond: 'AND',
  is_sale: TRI_STATE.NA,
  is_order: TRI_STATE.NA,
  is_prepay: TRI_STATE.NA,
  escalate_to: '',
  on_marafon: '',
  is_default: false,
};

const AgentFormDialog = ({ open, handleClose, agentId, onSuccess }) => {
  const [formData, setFormData] = useState(initialAgentForm);
  const [errors, setErrors] = useState({});
  const { data: agentData } = useGetAgentQuery(agentId, { skip: !agentId });
  const [createAgent] = useCreateAgentMutation();
  const [updateAgent] = useUpdateAgentMutation();
  const { showToast } = useToast();

  useEffect(() => {
    if (agentId && agentData?.item) {
      setFormData({
        url: agentData.item.url || '',
        name: agentData.item.name || '',
        token: agentData.item.token || '',
        enabled: agentData.item.enabled ?? true,
        agent_type: agentData.item.agent_type || 1,
        run_type: agentData.item.run_type || RunTypeEnum.CHAT,
        priority: agentData.item.priority ?? 0,
        last_marafon: agentData.item.last_marafon ?? TRI_STATE.NA,
        cur_date: agentData.item.cur_date || 0,
        plus_days: agentData.item.plus_days || '',
        cur_date_to: agentData.item.cur_date_to || 0,
        plus_days_to: agentData.item.plus_days_to || '',
        web1: agentData.item.web1 ?? TRI_STATE.NA,
        web2: agentData.item.web2 ?? TRI_STATE.NA,
        web_faq: agentData.item.web_faq ?? TRI_STATE.NA,
        cond: agentData.item.cond || 'AND',
        is_sale: agentData.item.is_sale ?? TRI_STATE.NA,
        is_order: agentData.item.is_order ?? TRI_STATE.NA,
        is_prepay: agentData.item.is_prepay ?? TRI_STATE.NA,
        escalate_to: agentData.item.escalate_to || '',
        on_marafon: agentData.item.on_marafon || '',
        is_default: agentData.item.is_default || false,
      });
    } else if (!agentId) {
      setFormData(initialAgentForm);
    }
  }, [agentData, agentId]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    // Special handling for plus_days and plus_days_to
    if (name === 'plus_days' || name === 'plus_days_to') {
      const cleanValue = value.replace(/[^\d,]/g, '');
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'priority' ? parseInt(value) || 0 : (type === 'checkbox' ? checked : value),
      ...(name === 'last_marafon' && (value === TRI_STATE.NO.toString() || value === TRI_STATE.NA.toString()) ? {
        cur_date: 0,
        plus_days: '',
        cur_date_to: 0,
        plus_days_to: '',
      } : {})
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.url.trim()) {
      newErrors.url = 'URL is required';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.token.trim()) {
      newErrors.token = 'Token is required';
    }
    if (!formData.agent_type) {
      newErrors.agent_type = 'Agent type is required';
    }
    if (!Number.isInteger(formData.priority)) {
      newErrors.priority = 'Priority must be an integer';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        agent_type: parseInt(formData.agent_type),
        priority: parseInt(formData.priority) || 0,
        is_default: !!formData.is_default,
      };

      if (agentId) {
        await updateAgent({ id: agentId, ...submitData }).unwrap();
        showToast('Agent updated successfully', 'success');
      } else {
        await createAgent(submitData).unwrap();
        showToast('Agent created successfully', 'success');
      }
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error saving agent:', error);
      showToast(error.data?.message || 'Error saving agent', 'error');
    }
  };

  function isMarafonAgent() {
    return formData.agent_type !== 1 && formData.agent_type !== 2 && formData.agent_type !== 4;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{agentId ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="URL"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              error={!!errors.url}
              helperText={errors.url}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Token"
              name="token"
              value={formData.token}
              onChange={handleInputChange}
              error={!!errors.token}
              helperText={errors.token}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Priority"
              name="priority"
              type="number"
              value={formData.priority}
              onChange={handleInputChange}
              error={!!errors.priority}
              helperText={errors.priority}
              sx={{ mb: 2 }}
              inputProps={{ step: 1 }}
            />
            <FormControl fullWidth error={!!errors.agent_type} sx={{ mb: 2 }}>
              <InputLabel>Agent Type</InputLabel>
              <Select
                name="agent_type"
                value={formData.agent_type}
                onChange={handleInputChange}
                label="Agent Type"
              >
                {Object.entries(AGENT_TYPES).map(([value, label]) => (
                  <MenuItem key={value} value={parseInt(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {errors.agent_type && (
                <Typography color="error" variant="caption">
                  {errors.agent_type}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth error={!!errors.run_type} sx={{ mb: 2 }}>
              <InputLabel>Run Type</InputLabel>
              <Select
                name="run_type"
                value={formData.run_type}
                onChange={handleInputChange}
                label="Run Type"
              >
                {Object.entries(RUN_TYPES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {errors.run_type && (
                <Typography color="error" variant="caption">
                  {errors.run_type}
                </Typography>
              )}
            </FormControl>
            {/* Last Marathon + On Marathon */}
            {isMarafonAgent() && <><Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Last Marathon</InputLabel>
                <Select
                  name="last_marafon"
                  value={formData.last_marafon}
                  onChange={handleInputChange}
                  label="Last Marathon"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {+formData.last_marafon === TRI_STATE.YES && (
                <TextField
                  fullWidth
                  label="On Marathon"
                  name="on_marafon"
                  value={formData.on_marafon}
                  onChange={handleInputChange}
                />
              )}
            </Box>
            {/* Cur. Date + Plus Days */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>Cur. Date</InputLabel>
                <Select
                  name="cur_date"
                  value={formData.cur_date}
                  onChange={handleInputChange}
                  label="Cur. Date"
                >
                  {Object.entries(DATE_COMPARISON_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.cur_date !== 0 && (
                <TextField
                  fullWidth
                  label="Plus Days"
                  name="plus_days"
                  value={formData.plus_days}
                  onChange={handleInputChange}
                  placeholder="Enter numbers, comma separated"
                />
              )}
            </Box>
            {/* Cur. Date To + Plus Days To */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>Cur. Date To</InputLabel>
                <Select
                  name="cur_date_to"
                  value={formData.cur_date_to}
                  onChange={handleInputChange}
                  label="Cur. Date To"
                >
                  {Object.entries(DATE_COMPARISON_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.cur_date_to !== 0 && (
                <TextField
                  fullWidth
                  label="Plus Days To"
                  name="plus_days_to"
                  value={formData.plus_days_to}
                  onChange={handleInputChange}
                  placeholder="Enter numbers, comma separated"
                />
              )}
            </Box>
            {/* Move AND to its own line before selects */}
            <Typography variant="body2" sx={{ textAlign: 'left', my: 0.5, fontWeight: 'bold' }}>
              AND
            </Typography>
            {/* All selects in one row: Web1, Condition, Web2, Condition, Web FAQ */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Web1</InputLabel>
                <Select
                  name="web1"
                  value={formData.web1}
                  onChange={handleInputChange}
                  label="Web1"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select
                  name="cond"
                  value={formData.cond}
                  onChange={handleInputChange}
                  label="Condition"
                >
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Web2</InputLabel>
                <Select
                  name="web2"
                  value={formData.web2}
                  onChange={handleInputChange}
                  label="Web2"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select
                  name="cond"
                  value={formData.cond}
                  onChange={handleInputChange}
                  label="Condition"
                >
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Web FAQ</InputLabel>
                <Select
                  name="web_faq"
                  value={formData.web_faq}
                  onChange={handleInputChange}
                  label="Web FAQ"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'left', my: 0.5, fontWeight: 'bold' }}>
              AND
            </Typography>
            {/* Order, Sale, Prepay, Escalate To in one row */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Order</InputLabel>
                <Select
                  name="is_order"
                  value={formData.is_order}
                  onChange={handleInputChange}
                  label="Order"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Sale</InputLabel>
                <Select
                  name="is_sale"
                  value={formData.is_sale}
                  onChange={handleInputChange}
                  label="Sale"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Prepay</InputLabel>
                <Select
                  name="is_prepay"
                  value={formData.is_prepay}
                  onChange={handleInputChange}
                  label="Prepay"
                >
                  {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={parseInt(value)}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="escalate-to-label">Escalate To</InputLabel>
                <Select
                  labelId="escalate-to-label"
                  name="escalate_to"
                  value={formData.escalate_to}
                  onChange={handleInputChange}
                  label="Escalate To"
                >
                  <MenuItem value="">N/A</MenuItem>
                  <MenuItem value="clients">Clients</MenuItem>
                  <MenuItem value="sales">Sales</MenuItem>
                </Select>
              </FormControl>
            </Box></>}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={handleInputChange}
                  name="enabled"
                />
              }
              label="Enabled"
              sx={{ mt: 2 }}
            />
            {isMarafonAgent() && false &&
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    is_default: e.target.checked
                  }))}
                  name="is_default"
                />
              }
              label="Use as default if other not found"
              sx={{ mt: 1, mb: 1 }}
            />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {agentId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Agents = () => {
  const [filters, setFilters] = useState(getSavedFilters());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    token: '',
    enabled: true,
    agent_type: 1,
    run_type: RunTypeEnum.CHAT
  });
  const { showToast } = useToast();

  const { data: agentsData, isLoading } = useGetAgentsQuery(filters);
  const [deleteAgent] = useDeleteAgentMutation();

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleFilterChange('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    handleFilterChange('limit', newRowsPerPage);
  };

  const handleDelete = async (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        await deleteAgent(agentId).unwrap();
        showToast('Agent deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting agent:', error);
        showToast(error.data?.message || 'Error deleting agent', 'error');
      }
    }
  };

  const handleOpenCreateDialog = () => {
    setSelectedAgentId(null);
    setFormData({
      url: '',
      name: '',
      token: '',
      enabled: true,
      agent_type: 1,
      run_type: RunTypeEnum.CHAT
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (agentId) => {
    setSelectedAgentId(agentId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAgentId(null);
  };

  const handleSuccess = () => {
    // Refresh the data
    handleSearch();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Agents</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
        >
          Create Agent
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agentsData?.items?.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>{agent.name}</TableCell>
                    <TableCell>{agent.url}</TableCell>
                    <TableCell>{AGENT_TYPES[agent.agent_type]}</TableCell>
                    <TableCell>{agent.priority ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={agent.enabled ? 'Enabled' : 'Disabled'}
                        color={agent.enabled ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenEditDialog(agent.id)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(agent.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={agentsData?.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <AgentFormDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        agentId={selectedAgentId}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default Agents; 